/*!

=========================================================
* Argon Dashboard React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, {
  useEffect,
  useRef,
  useContext,
  Suspense,
} from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import HeaderContainer from "components/Headers/HeaderContainer";
import ThinHeader from "components/Headers/ThinHeader";

import Sidebar from "components/Sidebar/Sidebar";
import Loader from "components/Loader";
import LoadFailed from "components/LoadFailed";

import routes from "routes";
import keys from "configs/constants";
import {
  setSites,
  setGateways,
  setAlerts,
  setControllers,
  updateCurrentPage,
} from "../redux/actions/index";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { AuthorizationContext } from "../contexts/AuthorizationContext";
import api from "../services/backendService";
import PropTypes from "prop-types";
import utils from "../utils";
import {isFeatureEnabled} from "../redux/selectors";
import { useSignalREvent, useSignalRReconnect } from "../hooks/useSignalREvent";
import { useReconcileOnVisible } from "../hooks/useReconcileOnVisible";
import { reconcileDashboardStateAsync } from "../redux/actions/thunk";
import MaintenanceBanner from "../components/MaintenanceBanner/MaintenanceBanner";
import useMaintenanceBanner from "../hooks/useMaintenanceBanner";
import useIncidentBanner from "../hooks/useIncidentBanner";
import useBannerOffset from "../hooks/useBannerOffset";
import { useCanSeeApiPage } from "../hooks/useApiAccess";
import useInitialLoad from "../hooks/useInitialLoad";
import { useTranslation } from "react-i18next";

// Mobile browsers freeze JS timers while a tab is backgrounded, so the
// SignalR connection can die server-side without the client noticing (it
// resumes against a stale connection on wake instead of tearing down and
// re-negotiating). Past this threshold we force a reconnect rather than trust
// the reported connection state, which can't be relied on right after a long
// suspension. Well under the "background >5 min" scenario this targets, to
// stay conservative for shorter switches.
const STALE_BACKGROUND_MS = 60000;

const Standard = (props) => {
  const context = useContext(AuthorizationContext);
  const location = useLocation();
  const { t } = useTranslation("common");
  const mContentRef = useRef();
  const { activeItem, visible, handleClose, handleDontShow } = useMaintenanceBanner();
  // Both banners drive useBannerOffset, which writes a single --banner-height,
  // one body padding and one sidebar offset. Two mounted banners would fight
  // over those, so an active incident takes precedence.
  const { active: incidentActive } = useIncidentBanner();
  const maintenanceVisible = visible && !incidentActive;
  const bannerRef = useRef();
  useBannerOffset(bannerRef, maintenanceVisible);
  const reconnectSignalR = useSignalRReconnect();
  const isSuperAdmin = context.isSuperAdmin();
  const activeOrgGuid =
    !isSuperAdmin && context.activeOrganization
      ? context.activeOrganization.guid
      : null;
  // Feature-flag + permission gating (shared hook, single source of truth).
  const canSeeApi = useCanSeeApiPage();
  // With the organization filter the store holds every organization, loaded
  // once (no customer) and scoped by the pages, so a filter change never
  // reloads it. A site page works in the site's own organization, which is
  // also what no customer asks the site's controllers in (template rights).
  const storeOrgGuid = context.isOrgFilterEnabled ? null : activeOrgGuid;

  const loadData = async (selectedOrgGuid) => {
    utils.debug("Standard.js: Loading data for organization", selectedOrgGuid);

    let response = await api.getDashboard(selectedOrgGuid);
    // An unready token answers { status: 0, data: null }: no dashboard to build from.
    if (!Array.isArray(response?.data?.data)) {
      throw new TypeError(
        `dashboard listing unavailable (status ${response?.status ?? "none"})`
      );
    }
    let apiSites = [];
    let apiGateways = [];
    for (let item of response.data.data) {
      const { site } = item;
      const { gateways } = site;

      apiSites.push(site);

      for (let gw of gateways) {
        apiGateways.push(gw);
      }
    }

    /* 
    Sites and gateways come from the call of the dashboard endpoint so the 
    functions to get entities initial states do not need to call API. 
    They are used just to get the proper format of data for the redux store
    */
    const sites = await api.getSiteInitialState(apiSites, true);
    props.setSites(sites);

    const gateways = await api.getGatewayInitialState(
      selectedOrgGuid,
      null,
      apiGateways,
      true
    );
    props.setGateways(gateways);

    props.setAlerts({ byId: {}, allIds: [] });

    if (location?.state?.site?.siteGuid) {
      utils.debug(
        "Standard.js: Loading controllers for site",
        location.state.site.siteGuid
      );
      const controllers = await api.getDevicesOnSite(
        location.state.site.siteGuid,
        storeOrgGuid
      );
      props.setControllers(controllers);
      utils.debug(
        "Standard.js: Controllers loaded for site",
        location.state.site.siteGuid
      );
    }

    const initialSites = await api.getSiteInitialState(null, false, selectedOrgGuid);
    props.setSites(initialSites);

    const initialGateways = await api.getGatewayInitialState(
      selectedOrgGuid,
      null,
      null,
      false
    );
    props.setGateways(initialGateways);

    const alerts = await api.getAlertInitialState();
    props.setAlerts(alerts);
  };

  // A listing that fails rejects (never a part of the list), so this load can
  // throw: show it with a retry instead of a loader that never goes away.
  const {
    isReady: isDataReady,
    hasFailed: dataLoadFailed,
    retry: retryDataLoad,
  } = useInitialLoad(context.isReady, () => loadData(storeOrgGuid), [
    storeOrgGuid,
  ]);

  // Reconcile the core store from REST whenever SignalR reconnects. During a
  // connection gap the at-most-once stream drops messages, leaving the UI
  // stale; this restores consistency automatically without a manual refresh.
  // It runs in the BACKGROUND via merge-by-id (RECONCILE_*), so unchanged
  // entities keep their reference and the UI does not reset like a fresh login
  // — the user's navigation, filters and in-progress edits are preserved.
  // useSignalREvent always invokes the latest closure, so it sees the current
  // activeOrgGuid / selected site.
  useSignalREvent("signalrReconnected", () => {
    if (!context.isReady) return;
    props.reconcileDashboardState(
      storeOrgGuid,
      location?.state?.site?.siteGuid
    );
  });

  // Same reconcile when the tab returns to the foreground: the at-most-once
  // stream can drop messages while backgrounded, so the store drifts (e.g.
  // alerts whose OFF was missed linger). Throttled; reference-preserving, so no
  // flicker. Restores consistency without a manual refresh.
  //
  // Past STALE_BACKGROUND_MS of hidden time, also force a SignalR reconnect:
  // a long background suspension (mobile OS freezing JS timers) can leave the
  // client stuck retrying against a dead connection (AB#45681) without the
  // reported connection state reflecting it. reconnectSignalR() already
  // triggers the "signalrReconnected" resync above, so no separate reconcile
  // call is needed on this path.
  useReconcileOnVisible((hiddenMs) => {
    if (!context.isReady) return;
    if (hiddenMs > STALE_BACKGROUND_MS) {
      reconnectSignalR();
      return;
    }
    props.reconcileDashboardState(
      storeOrgGuid,
      location?.state?.site?.siteGuid
    );
  });

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mContentRef.current) mContentRef.current.scrollTop = 0;
    props.updateCurrentPage(location);
  }, [location]);

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === "/std") {
        if (!props.isFleetPageEnabled && prop.name === "fleet") {
          return null;
        }
        if (prop.name === "api" && !canSeeApi) {
          return null;
        }
        const Comp = prop.component;
        return <Route path={prop.path} element={<Comp />} key={key} />;
      } else {
        return null;
      }
    });
  };

  const getBrandText = () => {
    for (let route of routes) {
      if (location.pathname.includes(route.layout + route.path)) {
        return route.name;
      }
    }
    return "brand";
  };

  return (
    <>
      <Sidebar
        {...props}
        routes={routes}
        logo={{
          imgAlt: "Logo",
          imgSrc: keys.LOGO_URL,
          innerLink: "/std/index",
        }}
      />
      <div className="main-content" ref={mContentRef}>
        <HeaderContainer>
          <ThinHeader {...props} brandText={getBrandText()} />
        </HeaderContainer>
        {isDataReady && (
          <Suspense fallback={<Loader />}>
            <Routes>
              {getRoutes(routes)}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        )}
        {dataLoadFailed && (
          <LoadFailed
            message={t("generic.loadFailed")}
            retryLabel={t("generic.retry")}
            onRetry={retryDataLoad}
          />
        )}
        {!isDataReady && !dataLoadFailed && <Loader />}
      </div>
      {maintenanceVisible && activeItem && (
        <MaintenanceBanner
          ref={bannerRef}
          item={activeItem}
          onClose={handleClose}
          onDontShow={handleDontShow}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    sites: state.sites.byId,
    isFleetPageEnabled: isFeatureEnabled("Fleet")(state),
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setControllers,
      setAlerts,
      setGateways,
      setSites,
      updateCurrentPage,
      reconcileDashboardState: reconcileDashboardStateAsync,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Standard);

Standard.propTypes = {
  auth: PropTypes.object.isRequired,
  store: PropTypes.object,
  setSites: PropTypes.func.isRequired,
  setGateways: PropTypes.func.isRequired,
  setAlerts: PropTypes.func.isRequired,
  setControllers: PropTypes.func.isRequired,
  updateCurrentPage: PropTypes.func.isRequired,
  reconcileDashboardState: PropTypes.func.isRequired,
  isFleetPageEnabled: PropTypes.bool,
};
